
import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import DashboardLoader from '@/components/dashboard/DashboardLoader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Database, Bot, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DatabaseSchemaMapper from '@/components/agent-builder/DatabaseSchemaMapper';
import AccessRequestCard from '@/components/dashboard/AccessRequestCard';

// Import required steps
import AgentPurposeStep from '@/components/agent-builder/steps/AgentPurposeStep';
import IndustryTemplateStep from '@/components/agent-builder/steps/IndustryTemplateStep';
import ArtifactsStep from '@/components/agent-builder/steps/ArtifactsStep';
import DatabaseMappingStep from '@/components/agent-builder/steps/DatabaseMappingStep';
import DeploymentStep from '@/components/agent-builder/steps/DeploymentStep';

const AgentBuilder = () => {
  const { user, profile, isLoading, signOut } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [agentData, setAgentData] = useState({
    name: '',
    purpose: '',
    llmService: '',
    industry: '',
    template: '',
    sopDocument: null,
    functions: [],
    artifacts: [],
    databaseSchema: '',
    generatedCode: ''
  });

  if (isLoading) {
    return <DashboardLoader />;
  }

  if (!user) {
    return <Navigate to="/auth" />;
  }

  if (profile?.role !== 'job_seeker') {
    return <Navigate to="/onboarding" />;
  }

  // Check if user has access to the Agent Builder
  if (!profile?.agent_builder_access) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-[#F0F9FF] to-[#E0F2FE] p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <DashboardHeader 
            firstName={profile?.first_name} 
            onSignOut={signOut} 
          />
          
          <AccessRequestCard 
            featureName="AI Agent Builder" 
            requestType="agent_builder" 
            userId={user.id} 
          />
        </div>
      </div>
    );
  }

  const steps = [
    {
      title: "Define Purpose",
      description: "Define what this agent will do and select the LLM service",
      component: <AgentPurposeStep
        agentData={agentData}
        setAgentData={setAgentData}
      />
    },
    {
      title: "Industry & Template",
      description: "Select industry, template and relevant SOP documents",
      component: <IndustryTemplateStep
        agentData={agentData}
        setAgentData={setAgentData}
        userId={user.id}
      />
    },
    {
      title: "Artifacts & Functions",
      description: "Define logical functions and associate artifacts",
      component: <ArtifactsStep
        agentData={agentData}
        setAgentData={setAgentData}
        userId={user.id}
      />
    },
    {
      title: "Database Mapping",
      description: "Map database schema fields to extracted information",
      component: <DatabaseMappingStep
        agentData={agentData}
        setAgentData={setAgentData}
        userId={user.id}
      />
    },
    {
      title: "Deploy",
      description: "Generate and deploy your agent",
      component: <DeploymentStep
        agentData={agentData}
        userId={user.id}
      />
    }
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-[#F0F9FF] to-[#E0F2FE] p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <DashboardHeader 
          firstName={profile?.first_name} 
          onSignOut={signOut} 
        />
        
        <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-[#0EA5E9]" />
              SOP Agent Builder
            </CardTitle>
            <CardDescription>
              Create AI agents to automate standard operating procedures for your organization
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                {steps.map((step, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <div 
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        index === currentStep 
                          ? 'bg-[#0EA5E9] text-white' 
                          : index < currentStep 
                            ? 'bg-green-500 text-white' 
                            : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {index + 1}
                    </div>
                    <span className="text-xs mt-1 hidden sm:block">{step.title}</span>
                  </div>
                ))}
              </div>
              <div className="h-1 w-full bg-gray-200 relative">
                <div 
                  className="h-1 bg-[#0EA5E9] absolute top-0 left-0 transition-all duration-300"
                  style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="py-4">
              <h3 className="text-xl font-semibold mb-2">{steps[currentStep].title}</h3>
              <p className="text-gray-600 mb-6">{steps[currentStep].description}</p>
              
              {steps[currentStep].component}
            </div>

            <div className="flex justify-between mt-6">
              <Button
                onClick={prevStep}
                disabled={currentStep === 0}
                variant="outline"
                className="flex items-center gap-2"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                onClick={nextStep}
                disabled={currentStep === steps.length - 1}
                className="bg-[#0EA5E9] text-white hover:bg-[#0EA5E9]/90 flex items-center gap-2"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AgentBuilder;
